/**
 * Cache management utilities for optimal repeat visit performance
 */

interface CacheConfig {
  name: string;
  version: number;
  maxAge: number; // in milliseconds
  maxSize: number; // in bytes
}

const CACHE_CONFIGS: Record<string, CacheConfig> = {
  // Static assets - cache for 1 year
  static: {
    name: 'static-assets-v1',
    version: 1,
    maxAge: 365 * 24 * 60 * 60 * 1000, // 1 year
    maxSize: 50 * 1024 * 1024, // 50MB
  },
  
  // API responses - cache for 1 hour
  api: {
    name: 'api-responses-v1',
    version: 1,
    maxAge: 60 * 60 * 1000, // 1 hour
    maxSize: 10 * 1024 * 1024, // 10MB
  },
  
  // Images - cache for 6 months
  images: {
    name: 'images-v1',
    version: 1,
    maxAge: 180 * 24 * 60 * 60 * 1000, // 6 months
    maxSize: 100 * 1024 * 1024, // 100MB
  },
  
  // Pages - cache for 1 week
  pages: {
    name: 'pages-v1',
    version: 1,
    maxAge: 7 * 24 * 60 * 60 * 1000, // 1 week
    maxSize: 20 * 1024 * 1024, // 20MB
  },
};

/**
 * Open cache with version management
 */
const openCache = async (config: CacheConfig): Promise<Cache> => {
  try {
    const cacheName = `${config.name}-${config.version}`;
    
    // Delete old versions
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter((name) => name.startsWith(config.name) && name !== cacheName)
        .map((name) => caches.delete(name))
    );
    
    return caches.open(cacheName);
  } catch (error) {
    console.error('Failed to open cache:', error);
    throw new Error(`Cache operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Cache a response with expiration
 */
export const cacheResponse = async (
  request: Request | string,
  response: Response,
  cacheType: keyof typeof CACHE_CONFIGS = 'static'
): Promise<void> => {
  if (!('caches' in window)) return;
  
  try {
    const config = CACHE_CONFIGS[cacheType];
    if (!config) {
      console.warn(`Unknown cache type: ${cacheType}`);
      return;
    }
    
    const cache = await openCache(config);
    
    // Clone response and add expiration header
    const clonedResponse = response.clone();
    const headers = new Headers(clonedResponse.headers);
    headers.set('X-Cache-Expires', (Date.now() + config.maxAge).toString());
    
    const cachedResponse = new Response(clonedResponse.body, {
      status: clonedResponse.status,
      statusText: clonedResponse.statusText,
      headers,
    });
    
    await cache.put(request, cachedResponse);
    
    // Enforce cache size limit
    await enforceCacheSize(config);
  } catch (error) {
    console.error('Failed to cache response:', error);
  }
};

/**
 * Get cached response if not expired
 */
export const getCachedResponse = async (
  request: Request | string,
  cacheType: keyof typeof CACHE_CONFIGS = 'static'
): Promise<Response | undefined> => {
  if (!('caches' in window)) return undefined;
  
  try {
    const config = CACHE_CONFIGS[cacheType];
    if (!config) {
      console.warn(`Unknown cache type: ${cacheType}`);
      return undefined;
    }
    
    const cache = await openCache(config);
    const response = await cache.match(request);
    
    if (!response) return undefined;
    
    // Check expiration
    const expiresHeader = response.headers.get('X-Cache-Expires');
    if (expiresHeader) {
      const expires = parseInt(expiresHeader, 10);
      if (Date.now() > expires) {
        // Expired - delete and return undefined
        await cache.delete(request);
        return undefined;
      }
    }
    
    return response;
  } catch (error) {
    console.error('Failed to get cached response:', error);
    return undefined;
  }
};

/**
 * Enforce cache size limit
 */
const enforceCacheSize = async (config: CacheConfig): Promise<void> => {
  try {
    const cache = await openCache(config);
    const requests = await cache.keys();
    
    let totalSize = 0;
    const entries: Array<{ request: Request; size: number; expires: number }> = [];
    
    // Calculate total size and collect entries
    for (const request of requests) {
      const response = await cache.match(request);
      if (!response) continue;
      
      const blob = await response.blob();
      const size = blob.size;
      totalSize += size;
      
      const expiresHeader = response.headers.get('X-Cache-Expires');
      const expires = expiresHeader ? parseInt(expiresHeader, 10) : Date.now();
      
      entries.push({ request, size, expires });
    }
    
    // If over limit, delete oldest entries
    if (totalSize > config.maxSize) {
      entries.sort((a, b) => a.expires - b.expires);
      
      for (const entry of entries) {
        if (totalSize <= config.maxSize) break;
        await cache.delete(entry.request);
        totalSize -= entry.size;
      }
    }
  } catch (error) {
    console.error('Failed to enforce cache size:', error);
  }
};

/**
 * Prefetch and cache resources
 */
export const prefetchAndCache = async (
  urls: string[],
  cacheType: keyof typeof CACHE_CONFIGS = 'static'
): Promise<void> => {
  if (!('caches' in window)) return;
  
  const config = CACHE_CONFIGS[cacheType];
  if (!config) {
    console.warn(`Unknown cache type: ${cacheType}`);
    return;
  }
  
  try {
    const cache = await openCache(config);
    
    await Promise.all(
      urls.map(async (url) => {
        try {
          // Check if already cached
          const cached = await cache.match(url);
          if (cached) return;
          
          // Fetch and cache
          const response = await fetch(url);
          if (response.ok) {
            await cacheResponse(url, response, cacheType);
          }
        } catch (error) {
          console.warn(`Failed to prefetch: ${url}`, error);
        }
      })
    );
  } catch (error) {
    console.error('Failed to prefetch and cache:', error);
  }
};

/**
 * Clear all caches
 */
export const clearAllCaches = async (): Promise<void> => {
  if (!('caches' in window)) return;
  
  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  } catch (error) {
    console.error('Failed to clear all caches:', error);
  }
};

/**
 * Clear specific cache type
 */
export const clearCache = async (
  cacheType: keyof typeof CACHE_CONFIGS
): Promise<void> => {
  if (!('caches' in window)) return;
  
  try {
    const config = CACHE_CONFIGS[cacheType];
    if (!config) {
      console.warn(`Unknown cache type: ${cacheType}`);
      return;
    }
    
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter((name) => name.startsWith(config.name))
        .map((name) => caches.delete(name))
    );
  } catch (error) {
    console.error(`Failed to clear cache for type: ${cacheType}`, error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  totalSize: number;
  cacheCount: number;
  caches: Array<{ name: string; size: number; count: number }>;
}> => {
  if (!('caches' in window)) {
    return { totalSize: 0, cacheCount: 0, caches: [] };
  }
  
  try {
    const cacheNames = await caches.keys();
    let totalSize = 0;
    const cacheStats: Array<{ name: string; size: number; count: number }> = [];
    
    for (const cacheName of cacheNames) {
      const cache = await caches.open(cacheName);
      const requests = await cache.keys();
      let cacheSize = 0;
      
      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const blob = await response.blob();
          cacheSize += blob.size;
        }
      }
      
      totalSize += cacheSize;
      cacheStats.push({
        name: cacheName,
        size: cacheSize,
        count: requests.length,
      });
    }
    
    return {
      totalSize,
      cacheCount: cacheNames.length,
      caches: cacheStats,
    };
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return { totalSize: 0, cacheCount: 0, caches: [] };
  }
};

/**
 * Warm up cache with critical resources
 */
export const warmUpCache = async (): Promise<void> => {
  if (!('caches' in window)) return;
  
  // Critical resources to cache immediately
  const criticalResources = [
    '/',
    '/rooms',
    '/blog',
    '/contact',
  ];
  
  // Prefetch critical pages during idle time
  const doPrefetch = () => {
    prefetchAndCache(criticalResources, 'pages').catch(error => {
      console.error('Failed to warm up cache:', error);
    });
  };
  
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      doPrefetch,
      { timeout: 2000 }
    );
  } else {
    setTimeout(doPrefetch, 2000);
  }
};

/**
 * Initialize cache management
 */
export const initializeCacheManagement = (): void => {
  // Warm up cache on load
  if (document.readyState === 'complete') {
    warmUpCache();
  } else {
    window.addEventListener('load', warmUpCache);
  }
  
  // Log cache stats in development
  // Check for development environment safely
  const isDevelopment = import.meta.env?.DEV || 
                        location.hostname === 'localhost' || 
                        location.hostname === '127.0.0.1' ||
                        location.hostname.includes('dev');
  
  if (isDevelopment) {
    getCacheStats().then((stats) => {
      console.log('Cache Statistics:', {
        totalSize: `${(stats.totalSize / 1024 / 1024).toFixed(2)}MB`,
        cacheCount: stats.cacheCount,
        caches: stats.caches.map((c) => ({
          name: c.name,
          size: `${(c.size / 1024).toFixed(2)}KB`,
          count: c.count,
        })),
      });
    }).catch(error => {
      console.error('Failed to log cache stats:', error);
    });
  }
};

/**
 * Fetch with cache-first strategy
 */
export const fetchWithCache = async (
  url: string,
  options?: RequestInit,
  cacheType: keyof typeof CACHE_CONFIGS = 'api'
): Promise<Response> => {
  try {
    // Try cache first
    const cached = await getCachedResponse(url, cacheType);
    if (cached) {
      return cached;
    }
    
    // Fetch from network
    const response = await fetch(url, options);
    
    // Cache successful responses
    if (response.ok) {
      await cacheResponse(url, response.clone(), cacheType);
    }
    
    return response;
  } catch (error) {
    console.error('Fetch with cache failed:', error);
    throw error;
  }
};

/**
 * Preload next page resources
 */
export const preloadNextPage = (path: string): void => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      prefetchAndCache([path], 'pages').catch(error => {
        console.warn('Failed to preload next page:', error);
      });
    });
  } else {
    setTimeout(() => {
      prefetchAndCache([path], 'pages').catch(error => {
        console.warn('Failed to preload next page:', error);
      });
    }, 100);
  }
};
